import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { SpecraTimeline } from '@mono/ui-components/captains';
import { SpecraButton, SpecraHeading, SpecraText } from '@mono/ui-components/ensigns';
import { SpecraKeyValueRow, SpecraRestrictedChip, SpecraUnverifiedClaim } from '@mono/ui-components/lieutenants';
import { Testids } from '@mono/ui-selectors';
import { renderRouterLink } from '../../../lib/renderRouterLink';
import { registryHref } from '../registryHref';
import { yearOf } from './model';
import { orgTone } from './orgTone';
const T = Testids.peopleMovement;
export const PERSON_HINT_SECTION = {
    id: 'person-hint',
    title: 'Person',
    children: /*#__PURE__*/ _jsx(SpecraText, {
        as: "p",
        size: "body-ui",
        tone: "muted",
        "data-testid": T.hint,
        children: "Select a person to see their career."
    })
};
/**
 * Rail sections for one person's career panel. Person-level pay and rates are
 * never read here: the rates row is a static restricted chip (comp vault).
 */ export function personRail(panel) {
    const contactHref = registryHref({
        kind: 'CONTACT',
        id: panel.contactId
    });
    const sections = [
        {
            id: 'person',
            title: 'Person',
            children: /*#__PURE__*/ _jsxs("div", {
                className: "space-y-[var(--space-2)]",
                "data-testid": T.personPanel,
                children: [
                    /*#__PURE__*/ _jsx(SpecraHeading, {
                        size: "ui",
                        children: panel.name
                    }),
                    panel.nowLine && /*#__PURE__*/ _jsx(SpecraText, {
                        as: "p",
                        size: "body-ui",
                        tone: "secondary",
                        children: panel.nowLine
                    }),
                    contactHref && /*#__PURE__*/ _jsx(SpecraButton, {
                        variant: "secondary",
                        size: "sm",
                        asChild: true,
                        "data-testid": T.openContact,
                        children: renderRouterLink({
                            href: contactHref,
                            children: 'Open contact'
                        })
                    })
                ]
            })
        },
        {
            id: 'career',
            title: 'Career',
            children: /*#__PURE__*/ _jsx(SpecraTimeline, {
                variant: "rail",
                emptyText: "No jobs recorded for this person yet.",
                items: panel.career.map((e, i)=>{
                    const from = yearOf(e.startDate);
                    const to = yearOf(e.endDate);
                    const note = e.endReason && e.endReason !== 'MOVED' ? e.endReason.toLowerCase() : '';
                    return {
                        id: e.employmentId || `${i}`,
                        title: e.employer?.name || e.employerText || 'Unknown employer',
                        date: `${from ?? '?'}–${to ?? 'now'}`,
                        description: [
                            e.title,
                            note
                        ].filter(Boolean).join(' · '),
                        markerTone: e.employer?.id ? orgTone(e.employer) : undefined
                    };
                })
            })
        }
    ];
    if (panel.whoKnows.length > 0) {
        sections.push({
            id: 'who-knows',
            title: 'Who knows them',
            children: /*#__PURE__*/ _jsx(SpecraText, {
                as: "p",
                size: "body-ui",
                children: panel.whoKnows.map((w)=>{
                    const year = w.lastMetAt ? ` ${w.lastMetAt.toDate().getUTCFullYear()}` : '';
                    return `${w.name}${w.contextHint || year ? ` (${[
                        w.contextHint,
                        year.trim()
                    ].filter(Boolean).join(', ')})` : ''}`;
                }).join(' · ')
            })
        });
    }
    if (panel.showsUpOn.length > 0) {
        sections.push({
            id: 'shows-up',
            title: 'Shows up on',
            children: /*#__PURE__*/ _jsx(SpecraText, {
                as: "p",
                size: "body-ui",
                children: panel.showsUpOn.map((r)=>r.name).join(' · ')
            })
        });
    }
    if (panel.notes.length > 0) {
        sections.push({
            id: 'notes',
            title: 'Note',
            children: /*#__PURE__*/ _jsx("div", {
                className: "space-y-[var(--space-2)]",
                children: panel.notes.map((n)=>/*#__PURE__*/ _jsx(SpecraUnverifiedClaim, {
                        claim: n.statement,
                        kind: n.claimKind === 'HANDLING_NOTE' ? 'note' : 'claim',
                        tone: n.claimKind === 'HANDLING_NOTE' ? 'neutral' : 'warning',
                        compact: true
                    }, n.id))
            })
        });
    }
    sections.push({
        id: 'rates',
        title: 'Rates',
        children: /*#__PURE__*/ _jsx(SpecraKeyValueRow, {
            label: "Person-level rates",
            value: /*#__PURE__*/ _jsx(SpecraRestrictedChip, {
                label: "Comp vault",
                reason: "Person-level pay and rates are visible only to the small vault group for each employer. Every read is logged.",
                "data-testid": T.compVault
            })
        })
    });
    return sections;
}
